import type { CardPaymentResultDto, CardRedirectDto, StorefrontEventContent } from '@xshop/shared';
import { fetchApi, getActiveStore } from '../api/client';
import { pixelTrackingPayload } from '../tracking/pixels';
import { trackingAllowed } from '../tracking/tracker';

// A card order between the checkout and Stripe's payment page: what the shop holds on to while the shopper is away,
// how it reads the address Stripe sends them back to, and the two calls it makes about the payment.

/** What the checkout keeps while a shopper pays on Stripe's page: the order placed, what it held, and its session. */
export type HeldCardOrder = { placed: unknown; contents: StorefrontEventContent[]; currency: string; sessionId: string };

/** How a shopper came back from Stripe: with the session they paid in, having given up, or not from Stripe at all. */
export type CardReturn = { sessionId: string | null; cancelled: boolean };

// Names the browser-session slot one shop keeps its card order in.
const slot = (): string => `xshop:card-order:${getActiveStore()?.slug ?? ''}`;

// Keeps a card order for the return from Stripe; a browser that refuses storage simply cannot confirm on return.
export function holdCardOrder(held: HeldCardOrder): void {
  try { window.sessionStorage.setItem(slot(), JSON.stringify(held)); } catch { /* storage blocked */ }
}

// Reads the card order kept for this shop, or null when there is none or it cannot be read.
export function heldCardOrder(): HeldCardOrder | null {
  try {
    const held = JSON.parse(window.sessionStorage.getItem(slot()) ?? 'null') as HeldCardOrder | null;
    return held && typeof held.sessionId === 'string' ? held : null;
  } catch { return null; }
}

// Forgets the kept card order once it is paid.
export function releaseCardOrder(): void {
  try { window.sessionStorage.removeItem(slot()); } catch { /* storage blocked */ }
}

// Reads the address Stripe sent a shopper back to.
export function cardReturnOf(search: string): CardReturn {
  const query = new URLSearchParams(search);
  const sessionId = query.get('stripe_session');
  return { sessionId: sessionId && /^cs_[A-Za-z0-9_]+$/.test(sessionId) ? sessionId : null, cancelled: query.get('stripe_cancelled') === '1' };
}

// Asks the shop whether a session paid its order, telling the conversion APIs who the shopper is when allowed.
export const confirmCardPayment = (sessionId: string): Promise<CardPaymentResultDto> => fetchApi<CardPaymentResultDto>('/payments/stripe/confirm', {
  method: 'POST', body: JSON.stringify({ sessionId, pixelTracking: trackingAllowed() ? pixelTrackingPayload() : undefined })
});

// Asks for Stripe's payment page again for an order its session left unpaid.
export const retryCardPayment = (sessionId: string): Promise<CardRedirectDto> => fetchApi<CardRedirectDto>('/payments/stripe/retry', {
  method: 'POST', body: JSON.stringify({ sessionId })
});
